import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMaterialNeedProjection } from '../../hooks';

const CommodityNeedProjection = ({
  materialCode,
  title: propTitle,
  description: propDescription
}) => {
  const { t, i18n } = useTranslation();
  // Fetch need projection data from API
  const { data: projection, loading, error } = useMaterialNeedProjection(materialCode);

  const title = propTitle ?? t("charts.needProjection.title");
  const description = propDescription ?? (projection?.description ||
    t("charts.needProjection.fallbackDescription"));

  const [data, setData] = useState([
    { year: '26', lowEstimate: 180, averageEst: 50, highEstimate: 40 },
    { year: '27', lowEstimate: 200, averageEst: 80, highEstimate: 60 },
    { year: '28', lowEstimate: 280, averageEst: 90, highEstimate: 70 },
    { year: '29', lowEstimate: 350, averageEst: 120, highEstimate: 80 },
    { year: '30', lowEstimate: 200, averageEst: 80, highEstimate: 70 },
    { year: '31', lowEstimate: 280, averageEst: 90, highEstimate: 50 },
    { year: '32', lowEstimate: 320, averageEst: 100, highEstimate: 40 }
  ]);

  const normalizeProjectionData = (rows = []) => (
    (rows || []).map((item) => {
      const low = Number(item?.lowEstimate ?? item?.low_estimate ?? 0);
      const avg = Number(item?.averageEst ?? item?.average_estimate ?? item?.avgEstimate ?? item?.avg_estimate ?? 0);
      const high = Number(item?.highEstimate ?? item?.high_estimate ?? 0);
      return {
        year: String(item?.year ?? ''),
        lowEstimate: Number.isFinite(low) ? low : 0,
        averageEst: Number.isFinite(avg) ? avg : 0,
        highEstimate: Number.isFinite(high) ? high : 0,
      };
    })
  );

  // Update data when API data is available
  useEffect(() => {
    if (projection?.data) {
      setData(normalizeProjectionData(projection.data));
    }
  }, [projection]);
  const [animatedData, setAnimatedData] = useState(data.map(item => ({
    ...item,
    lowEstimate: 0,
    averageEst: 0,
    highEstimate: 0
  })));
  const [tooltip, setTooltip] = useState(null);
  const chartRef = useRef(null);

  // Animation effect
  useEffect(() => {
    const duration = 1500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      const newAnimatedData = data.map(item => ({
        ...item,
        lowEstimate: item.lowEstimate * easeOutQuart,
        averageEst: item.averageEst * easeOutQuart,
        highEstimate: item.highEstimate * easeOutQuart
      }));
      
      setAnimatedData(newAnimatedData);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [data]);

  const [chartWidth, setChartWidth] = useState(980);

  // Keep chart width synced with container width so it truly fills the card.
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return undefined;

    const resize = () => {
      const nextWidth = Math.max(420, Math.floor(el.clientWidth || 0));
      setChartWidth(nextWidth);
    };

    resize();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => resize());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const chartHeight = 280;
  const padding = { top: 12, right: 20, bottom: 48, left: 50 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;

  const maxValue = useMemo(() => {
    const vals = data.map((item) => (
      Number(item.lowEstimate || 0) + Number(item.averageEst || 0) + Number(item.highEstimate || 0)
    ));
    const max = Math.max(...vals, 0);
    return max > 0 ? max : 1;
  }, [data]);

  const targetTicks = 5;
  const niceStep = useMemo(() => {
    const raw = maxValue / targetTicks;
    const power = Math.pow(10, Math.floor(Math.log10(raw || 1)));
    const normalized = raw / power;
    let nice = 1;
    if (normalized <= 1) nice = 1;
    else if (normalized <= 2) nice = 2;
    else if (normalized <= 2.5) nice = 2.5;
    else if (normalized <= 5) nice = 5;
    else nice = 10;
    return nice * power;
  }, [maxValue]);
  const yMax = Math.max(niceStep, Math.ceil(maxValue / niceStep) * niceStep);

  const formatAxisValue = (value) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    if (niceStep < 1) return value.toFixed(2);
    if (niceStep < 10) return value.toFixed(1);
    return value.toFixed(0);
  };

  const gridValues = [];
  for (let i = 0; i <= targetTicks; i += 1) {
    gridValues.push((yMax / targetTicks) * i);
  }

  const gridLines = gridValues.map((value) => {
    const y = padding.top + plotHeight - (value / yMax) * plotHeight;
    return (
      <g key={`grid-${value}`}>
        <line
          x1={padding.left}
          y1={y}
          x2={chartWidth - padding.right}
          y2={y}
          stroke="rgba(255, 255, 255, 0.2)"
          strokeDasharray="2,4"
          strokeWidth="1"
        />
        <text
          x={padding.left - 10}
          y={y + 4}
          fill="rgba(255, 255, 255, 0.7)"
          fontSize="11"
          textAnchor="end"
        >
          {formatAxisValue(value)}
        </text>
      </g>
    );
  });

  const groupWidth = plotWidth / Math.max(animatedData.length, 1);
  const barWidth = Math.max(18, Math.min(56, groupWidth * 0.6));

  const formatNum = (v) => {
    const num = typeof v === 'number' ? v : parseFloat(v);
    if (!Number.isFinite(num)) return v;
    const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('zh')
        ? 'zh-CN'
        : 'en-US';
    return num.toLocaleString(locale, { maximumFractionDigits: 0 });
  };

  const formatYear = (y) => {
    if (!y) return '';
    const str = String(y);
    return str.length === 2 ? `20${str}` : str;
  };

  const handleBarEnter = (item, event) => {
    const rect = chartRef.current?.getBoundingClientRect();
    const x = event.clientX - (rect?.left || 0);
    const y = event.clientY - (rect?.top || 0);
    setTooltip({
      item,
      x,
      y: Math.max(0, y - 40),
    });
  };

  const handleBarLeave = () => setTooltip(null);

  return (
    <div className="commodity-need-container">
      {/* Title */}
      <h2 className="commodity-need-title">
        {title}
      </h2>

      {/* Chart */}
      <div className="commodity-need-chart" ref={chartRef} style={{ position: 'relative' }}>
        <svg width={chartWidth} height={chartHeight} className="overflow-visible">
          {/* Grid lines and Y-axis labels */}
          {gridLines}
          
          {/* Bars */}
          {animatedData.map((item, index) => {
            const x = padding.left + index * groupWidth + (groupWidth - barWidth) / 2;

            // Add small gaps between segments
            const segmentGap = 2;

            // Calculate heights for each segment
            const lowHeight = Math.max(0, (item.lowEstimate / yMax) * plotHeight - segmentGap);
            const avgHeight = Math.max(0, (item.averageEst / yMax) * plotHeight - segmentGap);
            const highHeight = Math.max(0, (item.highEstimate / yMax) * plotHeight - segmentGap);

            // Calculate positions (stacked from bottom with gaps)
            const baseY = padding.top + plotHeight;
            const lowY = baseY - lowHeight;
            const avgY = lowY - avgHeight - segmentGap;
            const highY = avgY - highHeight - segmentGap;

            return (
              <g key={item.year}>
                {/* Low Estimate (bottom, dark blue) */}
                {item.lowEstimate > 0 && (
                  <rect
                    x={x}
                    y={lowY}
                    width={barWidth}
                    height={lowHeight}
                    fill="#1e40af"
                    rx="6"
                    ry="6"
                    onMouseEnter={(e) => handleBarEnter(item, e)}
                    onMouseMove={(e) => handleBarEnter(item, e)}
                    onMouseLeave={handleBarLeave}
                  />
                )}

                {/* Average Estimate (middle, teal) */}
                {item.averageEst > 0 && (
                  <rect
                    x={x}
                    y={avgY}
                    width={barWidth}
                    height={avgHeight}
                    fill="#06b6d4"
                    rx="6"
                    ry="6"
                    onMouseEnter={(e) => handleBarEnter(item, e)}
                    onMouseMove={(e) => handleBarEnter(item, e)}
                    onMouseLeave={handleBarLeave}
                  />
                )}

                {/* High Estimate (top, lighter teal) */}
                {item.highEstimate > 0 && (
                  <rect
                    x={x}
                    y={highY}
                    width={barWidth}
                    height={highHeight}
                    fill="#22d3ee"
                    rx="6"
                    ry="6"
                    onMouseEnter={(e) => handleBarEnter(item, e)}
                    onMouseMove={(e) => handleBarEnter(item, e)}
                    onMouseLeave={handleBarLeave}
                  />
                )}
                
                {/* X-axis labels */}
                <text
                  x={x + barWidth / 2}
                  y={chartHeight - padding.bottom + 20}
                  fill="rgba(255, 255, 255, 0.7)"
                  fontSize="14"
                  textAnchor="middle"
                >
                  {item.year.length === 2 ? `20${item.year}` : item.year}
                </text>
              </g>
            );
          })}
        </svg>
        {tooltip?.item && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translate(-50%, -100%)',
              background: 'rgba(17, 24, 39, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 8,
              padding: '10px 12px',
              boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
              color: '#fff',
              minWidth: 160,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 14 }}>{t('charts.commodity.year', { year: formatYear(tooltip.item.year) })}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: '#1e40af' }}>{t('charts.needProjection.low')}</span>
              <span>{formatNum(tooltip.item.lowEstimate)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: '#06b6d4' }}>{t('charts.needProjection.average')}</span>
              <span>{formatNum(tooltip.item.averageEst)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: '#22d3ee' }}>{t('charts.needProjection.high')}</span>
              <span>{formatNum(tooltip.item.highEstimate)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="commodity-need-legend">
        <div className="commodity-need-legend-item">
          <div className="commodity-need-legend-color" style={{ backgroundColor: '#1e40af' }}></div>
          <span className="commodity-need-legend-text">{t('charts.needProjection.lowEstimate')}</span>
        </div>
        <div className="commodity-need-legend-item">
          <div className="commodity-need-legend-color" style={{ backgroundColor: '#06b6d4' }}></div>
          <span className="commodity-need-legend-text">{t('charts.needProjection.averageEstimate')}</span>
        </div>
        <div className="commodity-need-legend-item">
          <div className="commodity-need-legend-color" style={{ backgroundColor: '#22d3ee' }}></div>
          <span className="commodity-need-legend-text">{t('charts.needProjection.highEstimate')}</span>
        </div>
      </div>

      {/* Description */}
      <p className="commodity-need-description">
        {description}
      </p>
    </div>
  );
};

export default CommodityNeedProjection;
